import { atom, useRecoilState } from "recoil";
import { useCallback } from "react";
import { localStorageDB } from "@/Data/LocalStorage";
import {
    fetchKoreanStudyTypeAPI,
    submitKoreanStudyTypeAPI,
    KoreanStudyTypeResult,
} from "@/Data/PersonaTest";

type Answer = 0 | 1 | 2 | 3 | null;

type KoreanStudyTypeState = {
    answers:  Answer[];
    results:  Record<string, KoreanStudyTypeResult>;
    loading:  boolean;
    error:    string | null;
};

const TOTAL_QUESTIONS = 10;
const STORAGE_KEY = 'personatest:koreanstudytype';

const loadFromStorage = (): Partial<KoreanStudyTypeState> => {
    try {
        const stored = localStorageDB.getItem(STORAGE_KEY);
        if (stored) { return JSON.parse(stored); }
    } catch (e) { console.error('Failed to load KoreanStudyType from storage:', e); }
    return {};
};

const saveToStorage = (state: KoreanStudyTypeState): void => {
    try {
        localStorageDB.setItem(STORAGE_KEY, JSON.stringify({ answers: state.answers, results: state.results }));
    } catch (e) { console.error('Failed to save KoreanStudyType to storage:', e); }
};

const defaultState: KoreanStudyTypeState = {
    answers:  Array(TOTAL_QUESTIONS).fill(null),
    results:  {},
    loading:  false,
    error:    null,
};

const koreanStudyTypeState = atom<KoreanStudyTypeState>({
    key: 'koreanStudyTypeState',
    default: { ...defaultState, ...loadFromStorage() },
});

/**
 * Hook for managing Korean Study Type persona test
 */
export const useKoreanStudyType = () => {
    const [state, setState] = useRecoilState(koreanStudyTypeState);

    const updateState = useCallback((newState: Partial<KoreanStudyTypeState>) => {
        setState(prev => {
            const updated = { ...prev, ...newState };
            saveToStorage(updated);
            return updated;
        });
    }, [setState]);

    const { answers, results, loading, error } = state;

    /**
     * Set answer for a specific question (0-indexed)
     */
    const setAnswer = useCallback((questionIndex: number, answer: 0 | 1 | 2 | 3) => {
        setState(prev => {
            const newAnswers = [...prev.answers];
            newAnswers[questionIndex] = answer;
            const updated = { ...prev, answers: newAnswers };
            saveToStorage(updated);
            return updated;
        });
    }, [setState]);

    /**
     * Get answer for a specific question (0-indexed)
     */
    const getAnswer = useCallback((questionIndex: number): Answer => {
        return answers[questionIndex] ?? null;
    }, [answers]);

    /**
     * Check if all questions are answered
     */
    const isComplete = useCallback((): boolean => {
        return answers.every(a => a !== null);
    }, [answers]);

    /**
     * Clear all answers
     */
    const clearAnswers = useCallback(() => {
        updateState({ answers: Array(TOTAL_QUESTIONS).fill(null) });
    }, [updateState]);

    /**
     * Fetch result for a specific user
     */
    const fetchResult = useCallback(async (addressid: string): Promise<KoreanStudyTypeResult | null> => {
        if (!addressid) { return null; }

        updateState({ loading: true, error: null });

        const data = await fetchKoreanStudyTypeAPI(addressid);

        if (data?.type) {
            setState(prev => {
                const updated = {
                    ...prev,
                    results: { ...prev.results, [addressid]: data },
                    loading: false,
                };
                saveToStorage(updated);
                return updated;
            });
            return data;
        }

        updateState({ loading: false, error: 'Failed to fetch result' });
        return null;
    }, [setState, updateState]);

    /**
     * Submit answers and get result
     */
    const submitResult = useCallback(async (addressid: string): Promise<KoreanStudyTypeResult | null> => {
        if (!isComplete()) {
            updateState({ error: 'Incomplete answers' });
            return null;
        }

        updateState({ loading: true, error: null });

        const validAnswers = answers.filter((a): a is 0 | 1 | 2 | 3 => a !== null);
        const data = await submitKoreanStudyTypeAPI(validAnswers);

        if (data?.type) {
            setState(prev => {
                const updated = {
                    ...prev,
                    results: { ...prev.results, [addressid]: data },
                    answers: prev.answers,
                    loading: false,
                };
                saveToStorage(updated);
                return updated;
            });
            return data;
        }

        updateState({ loading: false, error: 'Failed to submit result' });
        return null;
    }, [setState, updateState, answers, isComplete]);

    const reset = useCallback(() => {
        updateState({ ...defaultState });
    }, [updateState]);

    return {
        answers,
        results,
        loading,
        error,
        setAnswer,
        getAnswer,
        isComplete,
        clearAnswers,
        fetchResult,
        submitResult,

        reset,
    };
};
